import React from "react";
import { Input } from "reactstrap";

/**
 * The value an editor stores to say "intentionally no value" (SCRUM-496). The
 * public templates render it as "-" (SCRUM-1621); the visitor sort treats it as
 * missing and the amount filter excludes it.
 */
const NO_VALUE = "-";

/** The property types both product-property forms render as `type="number"`. */
const NUMERIC_PROPERTY_TYPES = ["float", "number", "currency", "interest"];

export const isNumericPropertyType = (type: string): boolean => NUMERIC_PROPERTY_TYPES.includes(type);

/**
 * A number input plus a "no value" toggle (SCRUM-1622). A `type="number"` input
 * refuses a lone "-", so the toggle is the only route to `NO_VALUE` for numeric
 * properties; `string` properties can type it directly and get no toggle.
 *
 * Shared by the Add and Update product-property forms, which are separate views
 * with separate change handlers — `onChange(name, value)` is the one seam both
 * already have.
 */
type NumericPropertyInputProps = {
  name: string;
  value: string | number;
  onChange: (name: string, value: string) => void;
};

const NumericPropertyInput = ({ name, value, onChange }: NumericPropertyInputProps) => {
  const noValue = value === NO_VALUE;

  return (
    <>
      <Input
        type="number"
        placeholder={name}
        name={name}
        value={noValue ? "" : value ?? ""}
        disabled={noValue}
        onChange={(e) => onChange(name, e.target.value)}
      />
      <label className="custom-toggle ml-3 mt-2" title="No value">
        <input
          type="checkbox"
          name={`${name}-no-value`}
          aria-label={`${name} no value`}
          checked={noValue}
          onChange={(e) => onChange(name, e.target.checked ? NO_VALUE : "")}
        />
        <span className="custom-toggle-slider rounded-circle" data-label-off="" data-label-on="-" />
      </label>
    </>
  );
};

export default NumericPropertyInput;
